import { useEffect, useMemo, useState, type ReactNode } from "react";
import qrcode from "qrcode-generator";
import type { PublicConventionGuide } from "@verity-connect/contracts";
import { BrandLockup } from "./BrandLockup";
import { Link } from "./router";
import {
  ATTENDEE_DESKTOP_QUERY,
  ATTENDEE_LIVE_NAV,
  attendeeLiveNavActive,
} from "./attendee-desktop";
import { blockProgress, dayTimeline, homeRows, scheduleDayId } from "./day-timeline";

export function useDesktopAttendeeViewport(): boolean {
  const [desktop, setDesktop] = useState(() => window.matchMedia(ATTENDEE_DESKTOP_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(ATTENDEE_DESKTOP_QUERY);
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return desktop;
}

export function AttendeePreparationHome({
  guide,
  currentBlockId,
  nextBlockId,
  savedItemIds,
  registered,
}: {
  guide: PublicConventionGuide;
  currentBlockId: string | null;
  nextBlockId: string | null;
  savedItemIds: readonly string[];
  registered: boolean;
}) {
  useEffect(() => { document.title = `Plan your Convention · WVCEA Connect`; }, []);
  usePageEntryFocus();
  const saved = useMemo(() => new Set(savedItemIds), [savedItemIds]);
  const seminarBlocks = guide.blocks.filter((block) => block.kind === "seminar");
  const selectedBlocks = seminarBlocks.filter((block) => block.itemIds.some((itemId) => saved.has(itemId))).length;
  const progress = seminarBlocks.length > 0 ? Math.round((selectedBlocks / seminarBlocks.length) * 100) : 0;
  const firstDay = guide.days[0];
  const dayRows = firstDay ? homeRows(dayTimeline(guide, saved, firstDay.id, currentBlockId, nextBlockId), 3) : [];
  const latestUpdate = guide.updates[0];

  return (
    <div className="attendee-preparation-home">
      <header className="attendee-preparation-heading">
        <div>
          <p className="attendee-preparation-kicker">Your Convention</p>
          <h1 data-page-heading tabIndex={-1}>Plan your Convention</h1>
          <p>{guide.event.venueName} · {guide.event.city}, {guide.event.state}</p>
        </div>
        <div className="attendee-preparation-meta">
          <time>{formatEventDates(guide.event.startDate, guide.event.endDate)}</time>
          {registered && <span>✓ Registration confirmed</span>}
        </div>
      </header>

      <div className="attendee-preparation-grid">
        <section className="attendee-preparation-finish" aria-labelledby="attendee-preparation-finish-title">
          <h2 id="attendee-preparation-finish-title">Finish your plan</h2>
          <p>Choose your seminars now so your schedule is ready when you arrive.</p>
          <strong>{selectedBlocks} of {seminarBlocks.length} seminar blocks selected</strong>
          <div className="attendee-preparation-progress" aria-label={`${progress} percent of seminar blocks selected`}><span style={{ width: `${progress}%` }} /></div>
          <Link className="attendee-desktop-primary" href="/schedule">Choose sessions</Link>
        </section>

        <section className="attendee-preparation-before" aria-labelledby="attendee-preparation-before-title">
          <h2 id="attendee-preparation-before-title">Before you arrive</h2>
          <nav aria-label="Arrival information">
            <Link href="/map?view=campus"><span aria-hidden="true">⌖</span><strong>Campus map</strong><b aria-hidden="true">›</b></Link>
            <Link href="/map?view=parking"><span aria-hidden="true">P</span><strong>Parking &amp; arrival</strong><b aria-hidden="true">›</b></Link>
            <Link href="/more/event"><span aria-hidden="true">i</span><strong>Convention details</strong><b aria-hidden="true">›</b></Link>
          </nav>
        </section>

        <section className="attendee-preparation-day" aria-labelledby="attendee-preparation-day-title">
          <h2 id="attendee-preparation-day-title">Your {firstDay?.label.split(",")[0] ?? "first day"}</h2>
          {dayRows.length > 0 ? <ol>{dayRows.map((row) => (
            <li key={row.key}>
              <time dateTime={row.startTime}>{formatClock(row.startTime)}</time>
              <Link href={row.href}><strong>{row.title}</strong><small>{row.awaitingChoice ? "Choose a session" : row.detail}</small></Link>
            </li>
          ))}</ol> : <p>No schedule has been published for the first day.</p>}
          <Link className="attendee-preparation-text-link" href="/schedule">View full schedule</Link>
        </section>

        <section className="attendee-preparation-updates" aria-labelledby="attendee-preparation-updates-title">
          <h2 id="attendee-preparation-updates-title">Convention updates</h2>
          {latestUpdate ? <div><strong>{latestUpdate.title}</strong><p>{latestUpdate.body}</p><Link href={latestUpdate.targetPath ?? "/updates"}>View update</Link></div>
            : <div className="attendee-preparation-caught-up"><span aria-hidden="true">✓</span><strong>You’re all caught up.</strong><p>No new official updates.</p></div>}
        </section>
      </div>

      <p className="attendee-preparation-phone-note"><span aria-hidden="true">☆</span>{registered
        ? "Your plan will be ready on your phone when the Convention begins."
        : "Sign in to carry your plan to your phone when the Convention begins."}</p>
    </div>
  );
}

export function AttendeeDesktopLanding({
  guide,
  currentBlockId,
  nextBlockId,
  savedItemIds,
  signedIn,
  syncStatus,
  requestedPath,
  offlineMessage,
  onContinue,
}: {
  guide: PublicConventionGuide;
  currentBlockId: string | null;
  nextBlockId: string | null;
  savedItemIds: readonly string[];
  signedIn: boolean;
  syncStatus: "device" | "syncing" | "synced" | "offline";
  requestedPath: string;
  offlineMessage: string | null;
  onContinue: () => void;
}) {
  useEffect(() => { document.title = `${guide.event.name} · WVCEA Connect`; }, [guide.event.name]);
  usePageEntryFocus();
  const saved = useMemo(() => new Set(savedItemIds), [savedItemIds]);
  const dayId = scheduleDayId(guide, currentBlockId, nextBlockId);
  const day = guide.days.find((candidate) => candidate.id === dayId) ?? guide.days[0];
  const plan = day ? dayTimeline(guide, saved, day.id, currentBlockId, nextBlockId) : [];
  const shown = homeRows(plan, 5);
  const focus = plan.find((row) => row.state === "now") ?? plan.find((row) => row.state === "next") ?? plan[0];
  const phoneUrl = new URL(requestedPath || "/", window.location.origin).toString();
  const planState = signedIn
    ? syncStatus === "offline" ? "Saved here; account sync will resume online" : syncStatus === "syncing" ? "Syncing with your account" : "Synced with your account"
    : "Saved on this browser";

  return (
    <div className="attendee-desktop-landing">
      <a className="skip-link" href="#desktop-landing-main">Skip to main content</a>
      <header className="attendee-desktop-landing-bar">
        <Link href="/home" aria-label="Attendee workspace"><BrandLockup size="small" /></Link>
        <Link href="/help">Help</Link>
      </header>
      {offlineMessage && <div className="offline-banner" role="status">{offlineMessage}</div>}
      <main id="desktop-landing-main">
        <header className="attendee-desktop-landing-heading">
          <h1 data-page-heading tabIndex={-1}>The Convention is underway</h1>
          <p>{formatEventDates(guide.event.startDate, guide.event.endDate)} · {guide.event.venueName}</p>
        </header>

        <div className="attendee-desktop-landing-grid">
          <section className="attendee-phone-handoff" aria-labelledby="attendee-phone-handoff-title">
            <div className="attendee-phone-handoff-copy">
              <h2 id="attendee-phone-handoff-title">Take WVCEA Connect with you</h2>
              <PhoneLinkSquare href={phoneUrl} />
              <p>{signedIn
                ? "Open the event on your phone and sign in there to use your synced plan and notes."
                : "Open the public event guide on your phone. Plans saved only in this browser stay on this browser."}</p>
              <a className="attendee-desktop-primary" href={phoneUrl}>Open WVCEA.app</a>
            </div>
            <div className="attendee-phone-preview" aria-hidden="true">
              <span className="attendee-phone-speaker" />
              <small>WVCEA Connect</small>
              <strong>{focus?.state === "now" ? "Happening now" : "Next up"}</strong>
              <h3>{focus?.title ?? guide.event.name}</h3>
              <p>{focus ? `${formatClock(focus.startTime)}${focus.roomLabel ? ` · ${focus.roomLabel}` : ""}` : formatEventDates(guide.event.startDate, guide.event.endDate)}</p>
              <span className="attendee-phone-preview-action">View details</span>
            </div>
          </section>

          <section className="attendee-desktop-plan" aria-labelledby="attendee-desktop-plan-title">
            <header><div><h2 id="attendee-desktop-plan-title">Your plan today</h2><p>{day?.label ?? guide.event.name}</p></div><small>{planState}</small></header>
            {shown.length > 0 ? <ol>{shown.map((row) => (
              <li key={row.key} className={row.state === "now" ? "is-now" : undefined}>
                <time dateTime={row.startTime}>{formatClock(row.startTime)}</time>
                <span aria-hidden="true" />
                <div><strong>{row.title}</strong><small>{row.detail}</small></div>
              </li>
            ))}</ol> : <p className="attendee-desktop-plan-empty">The published schedule has no Activities for this day.</p>}
            <div className="attendee-desktop-plan-actions">
              <Link href="/schedule?day=mine" onClick={onContinue}>View my plan</Link>
              <button type="button" onClick={() => window.print()}>Print or save PDF</button>
            </div>
          </section>
        </div>

        <section className="attendee-browser-choice" aria-labelledby="attendee-browser-choice-title">
          <div><h2 id="attendee-browser-choice-title">Need the browser version?</h2><p>You can still use the live Convention tools here when a phone is not available.</p></div>
          <button type="button" onClick={onContinue}>Continue in browser</button>
        </section>
      </main>
    </div>
  );
}

export function AttendeeLiveBrowserShell({
  guide,
  pathname,
  search,
  dayLabel,
  offlineMessage,
  onContinue,
  onBack,
  onOpenPhone,
  children,
}: {
  guide: PublicConventionGuide;
  pathname: string;
  search: string;
  dayLabel: string;
  offlineMessage: string | null;
  onContinue: () => void;
  onBack: () => void;
  onOpenPhone: () => void;
  children: ReactNode;
}) {
  const active = attendeeLiveNavActive(pathname, search);
  usePageEntryFocus();
  return (
    <div className="app-shell attendee-app-shell attendee-live-browser">
      <a className="skip-link" href="#attendee-live-main">Skip to main content</a>
      <header className="attendee-live-bar">
        <Link className="attendee-live-brand" href="/" onClick={onOpenPhone} aria-label="WVCEA Connect Convention landing"><BrandLockup size="small" /></Link>
        <Link className="attendee-live-back" href="/home" onClick={onBack}>← Attendee workspace</Link>
        <Link className="attendee-live-phone" href="/" onClick={onOpenPhone}><span aria-hidden="true">▯</span> Open on phone</Link>
      </header>
      <div className="attendee-live-frame">
        <header className="attendee-live-heading">
          <div>{pathname === "/"
            ? <h1 data-page-heading tabIndex={-1}>Today at the Convention</h1>
            : <p className="attendee-live-event-name">{guide.event.name}</p>}
            <p>{dayLabel} · {guide.event.venueName}</p></div>
        </header>
        <nav className="attendee-live-nav" aria-label="Convention">
          {ATTENDEE_LIVE_NAV.map((item) => <Link key={item.id} href={item.href} onClick={onContinue} aria-current={active === item.id ? "page" : undefined}>{item.label}</Link>)}
        </nav>
        <p className="attendee-live-notice"><span aria-hidden="true">ⓘ</span>You’re using the browser version. WVCEA Connect on your phone is best while moving around campus.</p>
        {offlineMessage && <div className="offline-banner" role="status">{offlineMessage}</div>}
        <main id="attendee-live-main">{children}</main>
      </div>
    </div>
  );
}

export function AttendeeDesktopToday({
  guide,
  currentBlockId,
  nextBlockId,
  now,
  savedItemIds,
}: {
  guide: PublicConventionGuide;
  currentBlockId: string | null;
  nextBlockId: string | null;
  now: Date;
  savedItemIds: readonly string[];
}) {
  useEffect(() => { document.title = `Today · ${guide.event.name}`; }, [guide.event.name]);
  const saved = useMemo(() => new Set(savedItemIds), [savedItemIds]);
  const dayId = scheduleDayId(guide, currentBlockId, nextBlockId);
  const rows = dayTimeline(guide, saved, dayId, currentBlockId, nextBlockId);
  const shown = homeRows(rows, 5);
  const focus = rows.find((row) => row.state === "now") ?? rows.find((row) => row.state === "next") ?? rows[0];
  const focusItem = focus?.itemId ? guide.items.find((item) => item.id === focus.itemId) : null;
  const presenters = focusItem?.presenterIds.map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName).filter(isString) ?? [];
  const progress = blockProgress(focus, now, guide.event.timeZone);
  const next = rows.find((row) => row.state === "next" && row.key !== focus?.key)
    ?? rows.find((row) => row.state === "ahead" && row.key !== focus?.key);
  const latestUpdate = guide.updates[0];

  return (
    <div className="attendee-desktop-today">
      <section className="attendee-today-timeline" aria-labelledby="attendee-today-timeline-title">
        <h2 id="attendee-today-timeline-title">Your day</h2>
        {shown.length > 0 ? <ol>{shown.map((row) => <li key={row.key} className={row.key === focus?.key ? "is-selected" : undefined}>
          <time dateTime={row.startTime}>{formatClock(row.startTime)}</time><span aria-hidden="true" />
          <Link href={row.href}><strong>{row.title}</strong><small>{row.state === "now" ? "Now" : row.detail}</small></Link>
        </li>)}</ol> : <p>The published schedule has no Activities for this day.</p>}
        <Link className="attendee-live-secondary" href="/schedule">View full schedule</Link>
      </section>

      <section className="attendee-today-detail" aria-labelledby="attendee-today-detail-title">
        <h2 id="attendee-today-detail-title">{focus?.title ?? "No current Activity"}</h2>
        {focus && <p className="attendee-today-detail-time">{formatTimeRange(focus.startTime, focus.endTime)}{focus.roomLabel ? ` · ${focus.roomLabel}` : ""}</p>}
        {presenters.length > 0 && <p className="attendee-today-presenters">{presenters.join(", ")}</p>}
        {focusItem?.organization && <p>{focusItem.organization}</p>}
        {focusItem ? <p className="attendee-today-description">{focusItem.description ?? "No description was supplied for this Activity."}</p>
          : focus?.awaitingChoice ? <p className="attendee-today-description">Choose one of the published sessions for this block.</p>
            : <p className="attendee-today-description">Open the full schedule for published details.</p>}
        {focus && <div className="attendee-today-actions">
          <Link className="attendee-desktop-primary" href={focus.href}>{focusItem ? "View Activity details" : "Choose a session"}</Link>
          {focusItem && <Link className="attendee-live-secondary" href={`${focus.href}?view=notes`}>Open my notes</Link>}
        </div>}
        {progress !== null && <div className="attendee-today-progress"><p><span aria-hidden="true" />Happening now</p><div aria-label={`${Math.round(progress * 100)} percent of this Activity has elapsed`}><i style={{ width: `${Math.round(progress * 100)}%` }} /></div></div>}
      </section>

      <aside className="attendee-today-context" aria-label="Convention context">
        <section><h2>Next up</h2>{next ? <><p><strong>{next.title}</strong><span>{formatClock(next.startTime)}{next.roomLabel ? ` · ${next.roomLabel}` : ""}</span></p><Link className="attendee-live-secondary" href={next.href}>{next.awaitingChoice ? "Choose a session" : "View details"}</Link></> : <p>No later Activity is published for this day.</p>}</section>
        <section><h2>{latestUpdate ? "Latest official update" : "Convention information"}</h2>{latestUpdate ? <><p><strong>{latestUpdate.title}</strong><span>{latestUpdate.body}</span></p><Link href={latestUpdate.targetPath ?? "/updates"}>View update</Link></> : <p>No official updates have been published.</p>}</section>
        <section className="attendee-today-map"><h2>Campus map</h2><Link href="/map"><img src="/maps/campus.jpg" alt="Appalachian Bible College campus map" /><span>Open map</span></Link></section>
      </aside>
    </div>
  );
}

function PhoneLinkSquare({ href }: { href: string }) {
  const square = useMemo(() => qrSvg(href), [href]);
  return <div className="attendee-phone-square"><div aria-hidden="true" dangerouslySetInnerHTML={{ __html: square }} /><span>Scan with your phone’s camera</span></div>;
}

function qrSvg(text: string): string {
  const code = qrcode(0, "M");
  code.addData(text);
  code.make();
  const count = code.getModuleCount();
  let path = "";
  for (let row = 0; row < count; row += 1) for (let column = 0; column < count; column += 1) {
    if (code.isDark(row, column)) path += `M${column} ${row}h1v1h-1z`;
  }
  return `<svg viewBox="-2 -2 ${count + 4} ${count + 4}" xmlns="http://www.w3.org/2000/svg"><rect x="-2" y="-2" width="${count + 4}" height="${count + 4}" fill="#fff"/><path d="${path}" fill="#071d43"/></svg>`;
}

function formatClock(value: string): string {
  const [hour = 0, minute = 0] = value.split(":").map(Number);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(new Date(2026, 0, 1, hour, minute));
}

function formatTimeRange(start: string, end: string): string {
  return `${formatClock(start)}–${formatClock(end)}`;
}

function formatEventDates(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T12:00:00Z`);
  const end = new Date(`${endDate}T12:00:00Z`);
  const month = (value: Date) => new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(value);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  if (startDate === endDate) return `${month(start)} ${start.getUTCDate()}, ${start.getUTCFullYear()}`;
  if (sameMonth) return `${month(start)} ${start.getUTCDate()}–${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  if (sameYear) return `${month(start)} ${start.getUTCDate()}–${month(end)} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  return `${month(start)} ${start.getUTCDate()}, ${start.getUTCFullYear()}–${month(end)} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
}

function isString(value: string | undefined): value is string {
  return typeof value === "string";
}

function usePageEntryFocus(): void {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("[data-page-heading]")?.focus();
    });
  }, []);
}
